import { useAuth0 } from "@auth0/auth0-react"
import { ErrorBoundary } from "react-error-boundary"
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useScrollToTop } from "../../hooks/useScrollToTop"
import { Header } from "../Header"
import { ErrorPage } from "../ErrorPage"
import { GeneralError } from "../../types/Error"

const is404 = (error: Error) =>
  error.cause?.name === "404" || error.message.match(/404/)

const isNloUser = (error: GeneralError) =>
  error?.response?.data?.message === "Invalid token"

export function MainLayout() {
  useScrollToTop()
  const { t } = useTranslation()
  const { province } = useParams<{ province: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth0()

  const isAdmin = location.pathname.match(/manage/)

  const onRetry = (
    error: Error,
    resetErrorBoundary: (...args: unknown[]) => void,
  ) => {
    if (is404(error)) {
      if (isAdmin) {
        navigate("/manage")
      } else {
        navigate(`/${province}`)
      }
    } else if (isNloUser(error)) {
      logout({ returnTo: window.location.origin })
    }
    resetErrorBoundary()
  }

  return (
    <div className="flex h-screen [font-family:var(--alias-font-body-lg-base-font-family)] [font-size:var(--alias-font-body-lg-base-font-size)]">
      <div className="flex-1 flex flex-col">
        <Header />
        <ErrorBoundary
          FallbackComponent={({ error, resetErrorBoundary }) => (
            <div className="flex flex-1 items-center justify-center h-[100vh]">
              <ErrorPage
                title={t("client.errorBoundary.title")}
                onRetry={() => onRetry(error, resetErrorBoundary)}
                retryText={
                  is404(error)
                    ? t("client.404.retry")
                    : isNloUser(error)
                    ? t("admin.topNav.logOut")
                    : undefined
                }
                errorMessage={
                  isNloUser(error)
                    ? t("client.errorBoundary.errorMessage")
                    : error.message
                }
              />
            </div>
          )}
        >
          <div className="mt-20">
            <Outlet />
          </div>
        </ErrorBoundary>
      </div>
    </div>
  )
}
